/**
 * SALES TARGETS — shared rules and wording for the Management "Sales targets"
 * tab (owner 2026-10-28; tab id `sales-targets`, never rename).
 *
 * One copy of each rule: the panel, table, cards, charts and export all import
 * from here. Never re-inline these.
 *
 * ⚠️ Every figure is a DECIMAL STRING in USD, converted server-side. Nothing
 * here converts a currency or calls `Number()` / `parseFloat` on money. `null`
 * is "not set / not measurable", never 0, and renders as '—'. The only
 * `Number()` in this file is on a PERCENTAGE, for a bar width (display only).
 */
import { sumDecimalStrings } from './decimal';
import { formatDecimalString } from './format';
import type {
  SalesTargetLine,
  SalesTargetLineFields,
  SalesTargetPool,
  SalesTargetTotals,
} from './types';

/* --- decimal-string helpers (exact, via lib/decimal.ts) ------------------- */

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

/** True when `value` is a plain decimal string ("12", "-3.50"). */
export function isDecimalString(value: string | null | undefined): value is string {
  return typeof value === 'string' && DECIMAL_RE.test(value.trim());
}

/** Flips the sign of a decimal string, exactly. */
function negate(value: string): string {
  const trimmed = value.trim();
  return trimmed.startsWith('-') ? trimmed.slice(1) : `-${trimmed}`;
}

/** `a − b`, exact. `null` when either side is not a decimal. */
export function subtractDecimal(a: string | null, b: string | null): string | null {
  if (!isDecimalString(a) || !isDecimalString(b)) return null;
  return sumDecimalStrings([a, negate(b)]);
}

/** -1 / 0 / 1 for `a` vs `b`, exact. `null` when either side is not a decimal. */
export function compareDecimal(a: string | null, b: string | null): -1 | 0 | 1 | null {
  const diff = subtractDecimal(a, b);
  if (diff === null) return null;
  if (/^-?0+(\.0+)?$/.test(diff)) return 0;
  return diff.startsWith('-') ? -1 : 1;
}

/** Exact sum of decimal strings; `null` entries are skipped, all-null → `null`. */
export function sumUsd(values: readonly (string | null | undefined)[]): string | null {
  return sumDecimalStrings([...values]);
}

/** Rounds a decimal string to whole units, half away from zero. */
function roundWhole(value: string): string {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [whole = '0', fraction = ''] = unsigned.split('.');
  let units = BigInt(whole);
  if (fraction !== '' && Number(fraction[0]) >= 5) units += 1n;
  if (units === 0n) return '0';
  return `${negative ? '-' : ''}${units.toString()}`;
}

/* --- formatting ------------------------------------------------------------ */

/**
 * A USD amount as text: "3,150.00 USD" (amount then ISO code, never a symbol,
 * like `<Money>`). `whole: true` rounds to dollars for charts and tight cells.
 * `null` / non-decimal → '—'. Prefer `<Money>` in JSX; this is for chart
 * labels, aria text and exports.
 */
export function formatUsd(
  value: string | null | undefined,
  options: { whole?: boolean; code?: boolean } = {},
): string {
  if (!isDecimalString(value)) return '—';
  const body = formatDecimalString(options.whole ? roundWhole(value) : value.trim());
  return options.code === false ? body : `${body} USD`;
}

/** A percentage decimal string as "84.5%" (trailing zeros dropped); `null` → '—'. */
export function formatPct(value: string | null | undefined): string {
  if (!isDecimalString(value)) return '—';
  const trimmed = value.trim();
  const tidy = trimmed.includes('.') ? trimmed.replace(/\.?0+$/, '') : trimmed;
  return `${formatDecimalString(tidy)}%`;
}

/** A count decimal string ("32.00" → "32", "2.50" → "2.5"); `null` → '—'. */
export function formatCount(value: string | null | undefined): string {
  if (!isDecimalString(value)) return '—';
  const trimmed = value.trim();
  const tidy = trimmed.includes('.') ? trimmed.replace(/\.?0+$/, '') : trimmed;
  return formatDecimalString(tidy);
}

/**
 * A percentage as a bar width in 0..cap. Display only (the one place a
 * percentage becomes a number); `null` → 0 width, the caller still shows '—'.
 */
export function pctWidth(value: string | null | undefined, cap = 100): number {
  if (!isDecimalString(value)) return 0;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, cap);
}

/* --- status tone ----------------------------------------------------------- */

export type SalesTargetTone = 'success' | 'neutral' | 'warning' | 'danger';

type ToneInput =
  | Pick<SalesTargetLine, 'achievedUsd' | 'targetRevenueUsd' | 'achievedPct' | 'paceGapUsd'>
  | Pick<SalesTargetPool, 'achievedUsd' | 'targetRevenueUsd' | 'achievedPct' | 'paceGapUsd'>
  | Pick<SalesTargetTotals, 'achievedUsd' | 'targetRevenueUsd' | 'achievedPct' | 'paceGapUsd'>;

/**
 * The status tone of a line, pool or totals row (owner 2026-10-28 rule):
 * - achieved ≥ 100% of target → `success`;
 * - achieved ≥ expected to date (on or ahead of pace) → `neutral`;
 * - behind pace → `warning`;
 * - achieved < 50% of expected to date → `danger`.
 *
 * Expected to date = achieved + paceGap (lines and pools carry `paceGapUsd`,
 * not `expectedToDateUsd`). Anything not measurable (no target, no achieved,
 * no pace) is `neutral`: an unknown is never painted as failing.
 */
export function achievedTone(row: ToneInput): SalesTargetTone {
  if (!isDecimalString(row.targetRevenueUsd)) return 'neutral';
  if (compareDecimal(row.targetRevenueUsd, '0') !== 1) return 'neutral';
  if (!isDecimalString(row.achievedUsd)) return 'neutral';

  const reached =
    compareDecimal(row.achievedPct, '100') ?? compareDecimal(row.achievedUsd, row.targetRevenueUsd);
  if (reached !== null && reached >= 0) return 'success';

  const behind = compareDecimal(row.paceGapUsd, '0');
  if (behind === null || behind <= 0) return 'neutral';

  // Behind pace. Danger when achieved × 2 < expected (achieved < 50% of expected).
  const expected = sumDecimalStrings([row.achievedUsd, row.paceGapUsd]);
  const doubled = sumDecimalStrings([row.achievedUsd, row.achievedUsd]);
  return compareDecimal(doubled, expected) === -1 ? 'danger' : 'warning';
}

/* --- product-family grouping ---------------------------------------------- */

export type SalesTargetFamilyKey =
  | 'business-school-festivals'
  | 'campus-tours'
  | 'stem'
  | 'virtual-events'
  | 'masterclasses'
  | 'digital-advertising'
  | 'services'
  | 'other';

/** Display order of the groups (stable; never sort by anything else). */
export const SALES_TARGET_FAMILIES: readonly { key: SalesTargetFamilyKey; label: string }[] = [
  { key: 'business-school-festivals', label: 'Business School Festivals' },
  { key: 'campus-tours', label: 'Campus Tours' },
  { key: 'stem', label: 'STEM' },
  { key: 'virtual-events', label: 'Virtual events' },
  { key: 'masterclasses', label: 'Masterclasses' },
  { key: 'digital-advertising', label: 'Digital Advertising' },
  { key: 'services', label: 'Services' },
  { key: 'other', label: 'Other' },
];

/*
 * ⚠️ DISPLAY GROUPING ONLY. This classifies by the product / project NAME,
 * which CLAUDE.md §2 forbids for behaviour ("never decide behaviour from
 * admin-editable names"). It is allowed here because it only decides which
 * visual group a row sits under: no figure, total, pool or permission depends
 * on it. A renamed product just lands in another group (or "Other"). Never
 * reuse this for maths. Checks run in this order (Masterclass before Virtual,
 * so "Virtual Masterclasses" is a masterclass).
 */
const FAMILY_MATCHERS: readonly { key: SalesTargetFamilyKey; re: RegExp }[] = [
  { key: 'masterclasses', re: /masterclass/i },
  { key: 'business-school-festivals', re: /business\s*school\s*festival|\bbsf\b/i },
  // STEM before Campus Tours: a STEM campus tour sits in STEM (owner 2026-10-28).
  { key: 'stem', re: /\bstem\b/i },
  { key: 'campus-tours', re: /campus\s*tour/i },
  { key: 'digital-advertising', re: /digital\s*advert|\bdac\b|\badvertis|\bads\b/i },
  { key: 'virtual-events', re: /virtual|webinar|online/i },
  { key: 'services', re: /service|evaluation|outreach|\blae\b|counsel|consult|report|profile/i },
];

/** The display family of a line, from its product name, else its project name. */
export function familyOf(
  line: Pick<SalesTargetLine, 'productName' | 'projectName'>,
): SalesTargetFamilyKey {
  for (const name of [line.productName, line.projectName]) {
    if (!name) continue;
    const hit = FAMILY_MATCHERS.find((m) => m.re.test(name));
    if (hit) return hit.key;
  }
  return 'other';
}

export interface SalesTargetLineGroup<T> {
  key: SalesTargetFamilyKey;
  label: string;
  lines: T[];
}

/**
 * Groups lines by product family in `SALES_TARGET_FAMILIES` order. Empty
 * groups are omitted; lines keep the server's order inside each group.
 */
export function groupLines<T extends Pick<SalesTargetLine, 'productName' | 'projectName'>>(
  lines: readonly T[],
): SalesTargetLineGroup<T>[] {
  const buckets = new Map<SalesTargetFamilyKey, T[]>();
  for (const line of lines) {
    const key = familyOf(line);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(line);
    else buckets.set(key, [line]);
  }
  return SALES_TARGET_FAMILIES.flatMap(({ key, label }) => {
    const bucket = buckets.get(key);
    return bucket ? [{ key, label, lines: bucket }] : [];
  });
}

/**
 * Subtotal of a group, excluding pool members' own plan/target (the pool row
 * carries those; no double count). Achieved always counts.
 */
export function subtotalLines(
  lines: readonly Pick<
    SalesTargetLine,
    'poolId' | 'plannedRevenueUsd' | 'targetRevenueUsd' | 'achievedUsd'
  >[],
): { plannedRevenueUsd: string | null; targetRevenueUsd: string | null; achievedUsd: string | null } {
  const own = lines.filter((l) => l.poolId === null);
  return {
    plannedRevenueUsd: sumUsd(own.map((l) => l.plannedRevenueUsd)),
    targetRevenueUsd: sumUsd(own.map((l) => l.targetRevenueUsd)),
    achievedUsd: sumUsd(lines.map((l) => l.achievedUsd)),
  };
}

/* --- editable line fields --------------------------------------------------- */

export type SalesTargetFieldKind = 'count' | 'percent' | 'months' | 'usd';

/** The numeric editable keys of a line (`poolId` and `notes` are edited separately). */
export type SalesTargetNumericField = Exclude<keyof SalesTargetLineFields, 'poolId' | 'notes'>;

export interface SalesTargetFieldMeta {
  key: SalesTargetNumericField;
  label: string;
  kind: SalesTargetFieldKind;
  min: number;
  /** `null` = no upper bound. */
  max: number | null;
  /** Decimal places allowed (the column is DECIMAL(x,2)). */
  decimals: number;
}

/** Labels match the Google Sheet columns. Order is the table's column order. */
export const EDITABLE_LINE_FIELDS: readonly SalesTargetFieldMeta[] = [
  { key: 'institutionPlanned', label: 'Planned capacity · Institution', kind: 'count', min: 0, max: null, decimals: 2 },
  { key: 'partnerPlanned', label: 'Planned capacity · Partner', kind: 'count', min: 0, max: null, decimals: 2 },
  { key: 'institutionTarget', label: 'Target · Institution', kind: 'count', min: 0, max: null, decimals: 2 },
  { key: 'partnerTarget', label: 'Target · Partner', kind: 'count', min: 0, max: null, decimals: 2 },
  { key: 'travelPercent', label: 'Travel %', kind: 'percent', min: 0, max: 100, decimals: 2 },
  { key: 'months', label: 'Months', kind: 'months', min: 0, max: null, decimals: 2 },
  { key: 'unitPriceOverrideUsd', label: 'Unit price USD', kind: 'usd', min: 0, max: null, decimals: 2 },
];

/** `notes` is VARCHAR(500). */
export const LINE_NOTES_MAX_LENGTH = 500;

/**
 * Parses what a person typed into an editable numeric cell. Blank → `null`
 * (clear); "5,000" → "5000"; a trailing "%" is accepted on percent fields.
 * Refuses non-numbers, too many decimals and out-of-range values with an
 * error message (errors are allowed UI text).
 */
export function parseLineFieldInput(
  meta: Pick<SalesTargetFieldMeta, 'kind' | 'min' | 'max' | 'decimals'>,
  raw: string,
): { ok: true; value: string | null } | { ok: false; error: string } {
  let text = raw.trim().replace(/,/g, '');
  if (meta.kind === 'percent') text = text.replace(/%$/, '').trim();
  if (meta.kind === 'usd') text = text.replace(/\s*USD$/i, '').trim();
  if (text === '') return { ok: true, value: null };
  if (!DECIMAL_RE.test(text)) return { ok: false, error: 'Enter a number.' };
  const fraction = text.split('.')[1] ?? '';
  if (fraction.length > meta.decimals) {
    return { ok: false, error: `Use at most ${meta.decimals} decimal places.` };
  }
  if (compareDecimal(text, String(meta.min)) === -1) {
    return { ok: false, error: `Must be at least ${meta.min}.` };
  }
  if (meta.max !== null && compareDecimal(text, String(meta.max)) === 1) {
    return { ok: false, error: `Must be at most ${meta.max}.` };
  }
  return { ok: true, value: text };
}
